import {Container} from '@mui/material';
import React, {useEffect, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  getPlayerSessionDetailAsync,
  updatePlayerSessionAsync,
} from '../../store/playerSessionSlice';
import {
  createSessionVideoSegmentAsync,
  deleteSessionVideoSegmentAsync,
  processSessionVideoAsync,
} from '../../store/sessionVideoSlice';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';
import {createSessionVideoSegmentComplete} from '../../api/sessionVideoAPI';
import {Helmet} from 'react-helmet';
import VideoManagement from '../shared/VideoManagement';
import {CurrentMembershipLevels, VideoTypes} from '../../enums';
import MembershipLocked from '../shared/MembershipLocked';
import PageTitle from '../shared/PageTitle';
import BackButton from '../shared/BackButton';
import {useTranslation} from 'react-i18next';
import ProfileIdentity from '../shared/ProfileIdentity';

interface Props {}

const PlayerSessionVideoDetail: React.FC<Props> = () => {
  const {t} = useTranslation();
  const {
    playerSessionId: playerSessionIdParam,
    videoId,
    teamId,
    profileId: profileIdParam,
  } = useParams();
  const playerSessionIdInt = useMemo(
    () => parseInt(playerSessionIdParam ?? ''),
    [playerSessionIdParam],
  );
  const videoIdInt = useMemo(() => parseInt(videoId ?? ''), [videoId]);
  const teamIdInt = useMemo(() => parseInt(teamId ?? ''), [teamId]);
  const profileIdInt = useMemo(
    () => parseInt(profileIdParam ?? ''),
    [profileIdParam],
  );
  const profileId = useAppSelector(state => state.profile?.profile?.id);
  const playerSessionProfileId = useAppSelector(
    state => state.playerSession?.playerSession?.player?.profile?.id,
  );
  const playerSessionProfileFirstName = useAppSelector(
    state => state.playerSession?.playerSession?.player?.profile?.firstName,
  );
  const playerSessionProfileLastName = useAppSelector(
    state => state.playerSession?.playerSession?.player?.profile?.lastName,
  );
  const playerSessionProfilePosition = useAppSelector(
    state => state.playerSession?.playerSession?.player?.profile?.position,
  );
  const playerSessionProfileProfilePictureUrl = useAppSelector(
    state =>
      state.playerSession?.playerSession?.player?.profile
        ?.profilePictureFileKey,
  );
  const playerSessionId = useAppSelector(
    state => state.playerSession?.playerSession?.id,
  );
  const playerSessionMembershipLevel = useAppSelector(
    state => state.playerSession?.playerSession?.membershipLevel,
  );
  const playerSessionVideoProcessingStartedAt = useAppSelector(
    state => state.playerSession?.playerSession?.videoProcessingStartedAt,
  );
  const playerSessionStartTime = useAppSelector(
    state => state.playerSession?.playerSession?.startTime,
  );
  const playerSessionEndTime = useAppSelector(
    state => state.playerSession?.playerSession?.endTime,
  );
  const playerSessionTimeZone = useAppSelector(
    state => state.playerSession?.playerSession?.timeZone,
  );
  const playerSessionPlayerSessionSessionEvents = useAppSelector(
    state =>
      state.playerSession?.playerSession?.playerSessionSessionEvents ?? [],
  );
  const sessionVideos = useAppSelector(state =>
    (state.playerSession?.playerSession?.sessionVideos ?? []).filter(
      sv => sv.id === videoIdInt,
    ),
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getPlayerSessionDetailAsync(playerSessionIdInt));
  }, [dispatch, playerSessionIdInt]);

  const title = useMemo(
    () =>
      t('navigation.pageTitle', {
        pageName: t('pages.playerSessionVideo'),
      }),
    [t],
  );

  if (!playerSessionId) {
    return (
      <>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <FullScreenLoadingSpinner />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <Container>
        <PageTitle>{t('pages.playerSessionVideo')}</PageTitle>
        <BackButton
          navigationUrl={
            teamIdInt && profileIdInt
              ? `/teams/${teamIdInt}/players/${profileIdInt}/playersessions/${playerSessionIdInt}`
              : profileIdInt
                ? `/family/children/${profileIdInt}/playersessions/${playerSessionIdInt}`
                : `/playersessions/${playerSessionIdInt}`
          }>
          {t('navigation.back', {pageName: t('pages.playerSessionDetail')})}
        </BackButton>
        {profileId !== playerSessionProfileId && (
          <ProfileIdentity
            firstName={playerSessionProfileFirstName}
            lastName={playerSessionProfileLastName}
            position={playerSessionProfilePosition}
            profilePictureUrl={playerSessionProfileProfilePictureUrl}
          />
        )}
        {playerSessionMembershipLevel === CurrentMembershipLevels.pro ? (
          <>
            <VideoManagement
              timeZone={playerSessionTimeZone}
              onTimeZoneChange={(timeZone: string) => {
                dispatch(
                  updatePlayerSessionAsync({
                    id: playerSessionIdInt,
                    params: {
                      timeZone,
                    },
                  }),
                );
              }}
              isProcessing={!!playerSessionVideoProcessingStartedAt}
              sessionEvents={playerSessionPlayerSessionSessionEvents ?? []}
              sessionVideoSegments={
                sessionVideos[0]?.sessionVideoSegments ?? []
              }
              sessionStartTime={playerSessionStartTime}
              sessionEndTime={playerSessionEndTime}
              refresh={async () =>
                dispatch(getPlayerSessionDetailAsync(playerSessionIdInt))
              }
              createSegment={async (params: {
                fileName?: string;
                parts?: number;
                veoUrl?: string;
                pixellotUrl?: string;
                playsightUrl?: string;
                userSelectedStartTime?: Date;
                userSelectedType?: VideoTypes;
              }) => {
                // unwrap() throws on a rejected thunk so callers can
                // detect segment creation failures instead of receiving
                // the rejected action's error payload as if it succeeded
                const payload = await dispatch(
                  createSessionVideoSegmentAsync({
                    sessionVideoId: videoIdInt,
                    fileName: params.fileName,
                    parts: params.parts,
                    veoUrl: params.veoUrl,
                    pixellotUrl: params.pixellotUrl,
                    playsightUrl: params.playsightUrl,
                    userSelectedStartTime: params.userSelectedStartTime,
                    userSelectedType: params.userSelectedType,
                  }),
                ).unwrap();
                return {payload};
              }}
              createSegmentComplete={async (params: {
                uploadId: string;
                key: string;
                parts: object[];
              }) =>
                createSessionVideoSegmentComplete({
                  sessionVideoId: videoIdInt,
                  uploadId: params.uploadId,
                  key: params.key,
                  parts: params.parts,
                })
              }
              deleteSegment={async (id: number) =>
                dispatch(
                  deleteSessionVideoSegmentAsync({
                    sessionVideoSegmentId: id,
                  }),
                )
              }
              processVideo={async () =>
                dispatch(
                  processSessionVideoAsync({
                    sessionVideoId: videoIdInt,
                  }),
                )
              }
            />
          </>
        ) : (
          <MembershipLocked minMembershipLevel={CurrentMembershipLevels.pro} />
        )}
      </Container>
    </>
  );
};

export default PlayerSessionVideoDetail;
